import type { DocumentoExportable, FilaExportable } from "@/componentes/exportar/modelo"

/**
 * Los indicadores de Planificación estratégica como documento (29/10): las tarjetas
 * de la pantalla, una fila por indicador agrupada por departamento, y aparte el
 * cierre de cada trimestre.
 *
 * Sólo lo que hace falta de `Indicador` (`Indicadores.tsx`): el tipo es estructural.
 */
type IndicadorExportable = {
  nombre: string
  departamento: string
  unidad: string
  sentido: "MAYOR" | "MENOR"
  linea_base: string | null
  meta: string
  tolerancia: number
  frecuencia: "MENSUAL" | "TRIMESTRAL"
  activo: boolean
  cumplimiento: string | null
  semaforo: string
  mediciones: { periodo: string; valor: string }[]
  trimestres: { trimestre: string; valor: string; cumplimiento: string | null; semaforo: string }[]
}

const SEMAFORO: Record<string, string> = {
  VERDE: "En meta",
  AMARILLO: "Cerca",
  ROJO: "Desvío",
  GRIS: "Sin medir",
}

const num = (v: string | null | undefined) => (v === null || v === undefined ? null : Number(v))

export function documentoDeIndicadores(lista: IndicadorExportable[]): DocumentoExportable {
  const porDepto = new Map<string, IndicadorExportable[]>()
  for (const i of lista) porDepto.set(i.departamento, [...(porDepto.get(i.departamento) ?? []), i])

  const filas: FilaExportable[] = []
  const cierres: FilaExportable[] = []
  for (const [depto, indicadores] of porDepto) {
    filas.push({ celdas: [depto, ...Array(10).fill(null)], grupo: true })
    for (const i of indicadores) {
      const ultima = i.mediciones.at(-1)
      filas.push({
        celdas: [
          i.activo ? i.nombre : `${i.nombre} (inactivo)`,
          i.unidad,
          i.sentido === "MAYOR" ? "Mayor es mejor" : "Menor es mejor",
          i.frecuencia === "MENSUAL" ? "Mensual" : "Trimestral",
          num(i.linea_base),
          num(i.meta),
          i.tolerancia,
          num(ultima?.valor),
          ultima ? ultima.periodo.slice(0, 10) : null,
          num(i.cumplimiento),
          SEMAFORO[i.semaforo] ?? i.semaforo,
        ],
      })
      for (const t of i.trimestres) {
        cierres.push({
          celdas: [
            depto,
            i.nombre,
            t.trimestre,
            num(t.valor),
            i.unidad,
            num(t.cumplimiento),
            SEMAFORO[t.semaforo] ?? t.semaforo,
          ],
        })
      }
    }
  }

  const rojos = lista.filter((i) => i.semaforo === "ROJO" && i.activo).length
  return {
    titulo: "Indicadores de gestión",
    subtitulo: `${lista.length} indicadores · ${rojos} con desvío`,
    secciones: [
      {
        titulo: "Indicadores por departamento",
        columnas: [
          { titulo: "Indicador", tipo: "texto" },
          { titulo: "Unidad", tipo: "texto" },
          { titulo: "Sentido", tipo: "texto" },
          { titulo: "Frecuencia", tipo: "texto" },
          { titulo: "Línea base", tipo: "monto" },
          { titulo: "Meta", tipo: "monto" },
          { titulo: "Tolerancia (%)", tipo: "numero" },
          { titulo: "Última medición", tipo: "monto" },
          { titulo: "Medido al", tipo: "fecha" },
          { titulo: "Cumplimiento (%)", tipo: "monto" },
          { titulo: "Semáforo", tipo: "texto" },
        ],
        filas,
      },
      ...(cierres.length > 0
        ? [
            {
              titulo: "Cierres trimestrales",
              columnas: [
                { titulo: "Departamento", tipo: "texto" as const },
                { titulo: "Indicador", tipo: "texto" as const },
                { titulo: "Trimestre", tipo: "texto" as const },
                { titulo: "Valor", tipo: "monto" as const },
                { titulo: "Unidad", tipo: "texto" as const },
                { titulo: "Cumplimiento (%)", tipo: "monto" as const },
                { titulo: "Semáforo", tipo: "texto" as const },
              ],
              filas: cierres,
            },
          ]
        : []),
    ],
  }
}
